/**
 * One set of solid color options of a single canvas element: the merchant defines one or more colors (or
 *  points at a color library) and the buyer picks one in the storefront. Image and vector fill, image outline
 *  and shadows are all this, they only differ in how the element stores its options (see the stores below).
 *
 * Out of reach: multi-element selections with mixed values, gradients and palettes. Text
 *  fill/stroke need all three, which is why TextColorProperties keeps its own logic.
 */
import { reactive, watch } from 'vue';
import { useColor } from '@/composables/color';
import type { CanvasEx } from '@/typings/canvas';
import type { ColorObject } from '@/typings/libraries';
import type { KeyValueDto } from '@/typings/keyValueDto';
import type { ElementMapOption } from '@/components/DesignStudio/types';

type ColorsMap = Map<number, ElementMapOption>;

export interface ColorOptionsElement {
    setEditedProp(...props: string[]): void;
}

/** How an element type stores and shows one set of color options. */
export interface ColorOptionsStore<T extends ColorOptionsElement> {
    /** The options keyed by their 1-based position. */
    getColors(element: T): ColorsMap;
    setColor(element: T, position: number, color: ElementMapOption): void;
    setColors(element: T, colors: ColorsMap): void;
    getOption(element: T): number | undefined;
    setOption(element: T, option: number): void;
    getLibraryId(element: T): number | undefined;
    setLibraryId(element: T, libraryId: number | undefined): void;
    /** Paints the element with the css an option resolves to. */
    paint(element: T, css: string): void;
    /** Painted, as option 1, when the requested option holds no color. */
    fallbackColor?(element: T): ElementMapOption | undefined;
    /** What gets marked as edited in edit mode when the colors, or the library, change. */
    editedProps: { colors: string[]; library: string };
}

export interface ColorOptionsConfig<T extends ColorOptionsElement> {
    store: ColorOptionsStore<T>;
    /** Getter, not the value: the selected element changes while the panel stays mounted. */
    element: () => T | undefined;
    canvas: () => CanvasEx;
    editMode: () => boolean | undefined;
    /** Opens the library picker, which lives in the AngularJS controller of each step. */
    openLibrarySelector: () => void;
}

/** The panel's copy of the element's options, the one ElementLibrarySelector is bound to. */
export interface ColorOptionsModel {
    colorsMap: ColorsMap;
    option: number;
    libraryId?: number;
}

export interface ColorOptionsApi {
    model: ColorOptionsModel;
    /** The css an option resolves to, without touching the element. */
    resolveColorCss: (option: number, value?: ElementMapOption) => string | undefined;
    loadColorIntoElement: (option: number, value?: ElementMapOption) => void;
    onColorSelected: (value: ElementMapOption, option?: number) => void;
    previewColorOption: (color?: ElementMapOption) => void;
    removeColorOption: (position: number) => void;
    onColorOptionsReordered: (reordered: KeyValueDto<number, ElementMapOption>[]) => void;
    /** Overwrites the whole option set and its library (a pasted clip, a reset...). */
    replaceColors: (colors: ColorsMap, libraryId?: number) => void;
    pickColorLibrary: () => void;
    removeColorLibrary: () => void;
}

export function useColorOptions<T extends ColorOptionsElement>(config: ColorOptionsConfig<T>): ColorOptionsApi {
    const { hexToRgba } = useColor();
    const { store } = config;

    const model = reactive<ColorOptionsModel>({
        colorsMap: new Map(),
        option: 1,
        libraryId: undefined
    });

    watch(
        config.element,
        (element) => {
            // Selecting nothing has to clear the panel too, or it keeps showing the previous element's options
            model.colorsMap = new Map(element ? store.getColors(element) : []);
            model.option = (element && store.getOption(element)) || 1;
            model.libraryId = element ? store.getLibraryId(element) ?? undefined : undefined;
        },
        { immediate: true }
    );

    const markEdited = (...props: string[]) => {
        if (config.editMode()) {
            config.element()?.setEditedProp(...props);
        }
    };

    const syncColors = (element: T) => {
        model.colorsMap = new Map(store.getColors(element));
    };

    const setOption = (element: T, option: number) => {
        store.setOption(element, option);
        model.option = option;
    };

    const toCss = (color: ColorObject) => (color.alpha < 1 ? hexToRgba(color.hex, color.alpha) : color.hex);

    const resolveColorCss = (option: number, value?: ElementMapOption) => {
        const element = config.element();
        const color = (value || (element && store.getColors(element).get(option))) as ColorObject | undefined;
        return color ? toCss(color) : undefined;
    };

    /** Resolves a color option to css and paints the element with it. */
    const loadColorIntoElement = (option: number, value?: ElementMapOption) => {
        const element = config.element();
        if (!element) return;

        let css = resolveColorCss(option, value);
        let appliedOption = option;
        if (!css && store.fallbackColor) {
            const fallback = store.fallbackColor(element) as ColorObject | undefined;
            css = fallback && toCss(fallback);
            appliedOption = 1;
        }
        if (!css) return;

        store.paint(element, css);
        setOption(element, appliedOption);

        config.canvas().requestRenderAll();
    };

    const onColorSelected = (value: ElementMapOption, option = 1) => {
        const element = config.element();
        if (!element || !value) return;

        markEdited(...store.editedProps.colors);
        store.setColor(element, option, value);
        syncColors(element);

        loadColorIntoElement(option);
    };

    /** Paints a color without storing it, so the merchant can try options out. */
    const previewColorOption = (color?: ElementMapOption) => {
        loadColorIntoElement(model.option || 1, color);
    };

    const removeColorOption = (position: number) => {
        const element = config.element();
        if (!element) return;

        markEdited(...store.editedProps.colors);
        const colors = new Map(store.getColors(element));
        colors.delete(position);
        store.setColors(element, colors);
        syncColors(element);

        // Positions keep their number when one is removed. The selector only ever removes the last
        //  option of the list and never the only one left, so there is always a first one to fall
        //  back to; reading it instead of hardcoding 1 keeps this right if that ever changes.
        const [firstOption] = colors.keys();
        loadColorIntoElement(firstOption ?? 1);
    };

    /** Positions are rebuilt from the new order; the selected color stays selected wherever it moved. */
    const onColorOptionsReordered = (reordered: KeyValueDto<number, ElementMapOption>[]) => {
        const element = config.element();
        if (!element) return;

        const current = store.getColors(element);
        const colors: ColorsMap = new Map();
        let selectedOption = 1;
        reordered.forEach(({ id, value }, index) => {
            // KeyValueDto declares both optional, but the selector only emits populated options
            colors.set(index + 1, (current.get(id as number) ?? value) as ElementMapOption);
            if (id === model.option) {
                selectedOption = index + 1;
            }
        });

        markEdited(...store.editedProps.colors);
        store.setColors(element, colors);
        syncColors(element);
        loadColorIntoElement(selectedOption);
    };

    const replaceColors = (colors: ColorsMap, libraryId?: number) => {
        const element = config.element();
        if (!element) return;

        markEdited(...store.editedProps.colors, store.editedProps.library);
        store.setColors(element, new Map(colors));
        store.setLibraryId(element, libraryId);
        syncColors(element);
        model.libraryId = libraryId;
        // Set explicitly instead of relying on loadColorIntoElement: a purely library-linked set (no
        //  manual colors) has nothing at position 1 to paint, so it would never get to set it.
        setOption(element, 1);
        loadColorIntoElement(1);
    };

    const pickColorLibrary = () => {
        const element = config.element();
        if (!element) return;

        setOption(element, 1);
        config.openLibrarySelector();
    };

    const removeColorLibrary = () => {
        const element = config.element();
        if (!element) return;

        store.setLibraryId(element, undefined);
        markEdited(store.editedProps.library);
        model.libraryId = undefined;
        loadColorIntoElement(1);
    };

    return {
        model,
        resolveColorCss,
        loadColorIntoElement,
        onColorSelected,
        previewColorOption,
        removeColorOption,
        onColorOptionsReordered,
        replaceColors,
        pickColorLibrary,
        removeColorLibrary
    };
}

/** Field names of a set of options the element keeps as a Map (shadow, image outline). */
export interface ColorsMapKeys {
    colorsMap: string;
    option: string;
    libraryId: string;
    /** Persisted copy of option 1, for the elements that keep one next to the map */
    defaultColor?: string;
}

// The callers type the element; in here its fields are only addressed by the names in ColorsMapKeys
const fieldsOf = (target: object) => target as Record<string, unknown>;

export function colorsMapStore<T extends ColorOptionsElement>(
    keys: ColorsMapKeys,
    paint: (element: T, css: string) => void
): ColorOptionsStore<T> {
    const getColors = (element: T) => (fieldsOf(element)[keys.colorsMap] as ColorsMap | undefined) ?? new Map();

    const setColors = (element: T, colors: ColorsMap) => {
        const fields = fieldsOf(element);
        fields[keys.colorsMap] = colors;
        // The default color has to follow option 1 every time the positions change. Texts get away without
        //  this because they re-derive it while saving (processTextBeforeSaving), image placeholders don't.
        if (keys.defaultColor) {
            fields[keys.defaultColor] = colors.get(1);
        }
    };

    return {
        getColors,
        setColor: (element, position, color) => setColors(element, new Map(getColors(element)).set(position, color)),
        setColors,
        getOption: (element) => fieldsOf(element)[keys.option] as number | undefined,
        setOption: (element, option) => {
            fieldsOf(element)[keys.option] = option;
        },
        getLibraryId: (element) => (fieldsOf(element)[keys.libraryId] as number | null | undefined) ?? undefined,
        setLibraryId: (element, libraryId) => {
            fieldsOf(element)[keys.libraryId] = libraryId;
        },
        paint,
        editedProps: {
            colors: keys.defaultColor ? [keys.colorsMap, keys.defaultColor] : [keys.colorsMap],
            library: keys.libraryId
        }
    };
}

/** An element that keeps its options as a list of positioned colors, stored as rows (image and vector fill). */
export interface ColorsListElement extends ColorOptionsElement {
    colors?: (ColorObject & { position: number })[];
    selectedColorPosition?: number;
    colorLibraryId?: number;
}

export function colorsListStore<T extends ColorsListElement>(
    paint: (element: T, css: string) => void,
    fallbackColor?: (element: T) => ElementMapOption | undefined
): ColorOptionsStore<T> {
    return {
        getColors: (element) => new Map((element.colors || []).map((color) => [color.position, color] as const)),
        setColor: (element, position, value) => {
            const color = value as ColorObject;
            element.colors = element.colors || [];
            // Updated in place: a stored color is a row that carries more than the picker hands over (its id)
            const stored = element.colors.find((c) => c.position === position);
            if (stored) {
                stored.hex = color.hex;
                stored.alpha = color.alpha;
                stored.cmyk = color.cmyk;
            } else {
                element.colors.push({ ...color, position });
            }
        },
        setColors: (element, colors) => {
            element.colors = Array.from(colors, ([position, color]) => ({ ...(color as ColorObject), position }));
        },
        getOption: (element) => element.selectedColorPosition,
        setOption: (element, option) => {
            element.selectedColorPosition = option;
        },
        getLibraryId: (element) => element.colorLibraryId,
        setLibraryId: (element, libraryId) => {
            element.colorLibraryId = libraryId;
        },
        paint,
        fallbackColor,
        editedProps: { colors: ['colors'], library: 'colorLibraryId' }
    };
}
